import { useMemo } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
  BarChart, Bar, Cell,
} from 'recharts';
import { TrendingUp, TrendingDown, Calendar, Activity } from 'lucide-react';

/**
 * Visualizations for a backtest result. Pure-data: takes the existing backtest
 * `result` object (trades + equityCurve) and renders charts.
 *
 * - EquityCurveChart: cumulative portfolio value over time
 * - DrawdownChart: peak-to-trough decline as % over time
 * - MonthlyReturnsHeatmap: returns by month, color-coded green/red
 *
 * Falls back to "Not enough data" if the result is missing required fields.
 */

function formatNum(v, dp = 2) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

// ─── Helpers — read both old and new backtest engine field names ────────────
//
// Old engine (historicalDataService.runRealBacktest) emits trades with:
//   exitTimestamp, entryTimestamp, pnl, pnlPct
// New engine (backtestEngine.runBacktest) emits trades with:
//   ts (ISO string or ms), pnlUsd, pnlPct, side
// We support both so the same visualization component works for Stress Test
// (new engine) and the older /backtest/run flow (old engine).
function tradeTs(t) {
  const raw = t.exitTimestamp || t.exitTime || t.ts || t.timestamp || 0;
  // ts can be ISO string OR milliseconds — Date() handles both, but for sort
  // we need a number.
  if (typeof raw === 'string') {
    const n = new Date(raw).getTime();
    return Number.isFinite(n) ? n : 0;
  }
  return Number(raw) || 0;
}
function tradePnl(t) {
  // New engine uses pnlUsd; old uses pnl
  return Number(t.pnlUsd ?? t.pnl ?? 0) || 0;
}

// ─── Equity curve ────────────────────────────────────────────────────────────

export function EquityCurveChart({ trades, initialCapital = 10000 }) {
  const data = useMemo(() => {
    if (!Array.isArray(trades) || trades.length === 0) return [];
    let equity = initialCapital;
    // Filter to closed trades only (have non-zero pnl OR explicit sell side)
    const closed = trades.filter(t => tradePnl(t) !== 0 || t.side === 'sell');
    const sorted = closed.slice().sort((a, b) => tradeTs(a) - tradeTs(b));
    return sorted.map(t => {
      equity += tradePnl(t);
      const ts = tradeTs(t);
      return {
        time: ts,
        equity,
        dateLabel: ts ? new Date(ts).toLocaleDateString() : '—',
      };
    });
  }, [trades, initialCapital]);

  if (data.length < 2) return <NoData label="Equity Curve" reason="Need at least 2 closed trades to chart" />;

  const finalEquity = data[data.length - 1].equity;
  const isProfit = finalEquity > initialCapital;

  return (
    <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold text-white flex items-center gap-2">
          {isProfit ? <TrendingUp className="w-4 h-4 text-emerald-400" /> : <TrendingDown className="w-4 h-4 text-red-400" />}
          Equity Curve
        </h4>
        <span className={`text-xs font-mono ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
          ${formatNum(initialCapital, 0)} → ${formatNum(finalEquity, 0)}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id="equityFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={isProfit ? '#10b981' : '#ef4444'} stopOpacity={0.3} />
              <stop offset="100%" stopColor={isProfit ? '#10b981' : '#ef4444'} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="dateLabel" tick={{ fontSize: 10, fill: '#6b7280' }} />
          <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} domain={['dataMin', 'dataMax']} tickFormatter={v => `$${(v / 1000).toFixed(1)}k`} />
          <Tooltip
            contentStyle={{ background: '#0d0e14', border: '1px solid #1f2937', fontSize: 11, borderRadius: 8 }}
            formatter={(v) => [`$${formatNum(v, 2)}`, 'Equity']}
          />
          <ReferenceLine y={initialCapital} stroke="#6b7280" strokeDasharray="3 3" label={{ value: 'Initial', position: 'right', fill: '#6b7280', fontSize: 10 }} />
          <Area type="monotone" dataKey="equity" stroke={isProfit ? '#10b981' : '#ef4444'} fill="url(#equityFill)" strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Drawdown chart ──────────────────────────────────────────────────────────

export function DrawdownChart({ trades, initialCapital = 10000 }) {
  const data = useMemo(() => {
    if (!Array.isArray(trades) || trades.length === 0) return [];
    let equity = initialCapital;
    let peak = initialCapital;
    const closed = trades.filter(t => tradePnl(t) !== 0 || t.side === 'sell');
    const sorted = closed.slice().sort((a, b) => tradeTs(a) - tradeTs(b));
    return sorted.map(t => {
      equity += tradePnl(t);
      if (equity > peak) peak = equity;
      const ddPct = peak > 0 ? ((equity - peak) / peak) * 100 : 0;
      const ts = tradeTs(t);
      return {
        time: ts,
        drawdown: ddPct,
        dateLabel: ts ? new Date(ts).toLocaleDateString() : '—',
      };
    });
  }, [trades, initialCapital]);

  if (data.length < 2) return <NoData label="Drawdown" reason="Need at least 2 closed trades to chart" />;
  const maxDD = Math.min(...data.map(d => d.drawdown));

  return (
    <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold text-white flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-red-400" />
          Drawdown
        </h4>
        <span className="text-xs font-mono text-red-400">
          Max: {formatNum(maxDD, 2)}%
        </span>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={data} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id="ddFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ef4444" stopOpacity={0} />
              <stop offset="100%" stopColor="#ef4444" stopOpacity={0.4} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="dateLabel" tick={{ fontSize: 10, fill: '#6b7280' }} />
          <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => `${v.toFixed(0)}%`} />
          <Tooltip
            contentStyle={{ background: '#0d0e14', border: '1px solid #1f2937', fontSize: 11, borderRadius: 8 }}
            formatter={(v) => [`${formatNum(v, 2)}%`, 'Drawdown']}
          />
          <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="3 3" />
          <Area type="monotone" dataKey="drawdown" stroke="#ef4444" fill="url(#ddFill)" strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Monthly returns heatmap ─────────────────────────────────────────────────

export function MonthlyReturnsHeatmap({ trades, initialCapital = 10000 }) {
  const monthlyData = useMemo(() => {
    if (!Array.isArray(trades) || trades.length === 0) return [];
    const buckets = {};
    for (const t of trades) {
      const ts = tradeTs(t);
      if (!ts) continue;
      const d = new Date(ts);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!buckets[key]) buckets[key] = { key, year: d.getFullYear(), month: d.getMonth() + 1, pnl: 0, trades: 0 };
      buckets[key].pnl += tradePnl(t);
      buckets[key].trades += 1;
    }
    return Object.values(buckets).sort((a, b) => a.key.localeCompare(b.key));
  }, [trades]);

  if (monthlyData.length === 0) return <NoData label="Monthly Returns" reason="Need closed trades with timestamps" />;

  // Color a bar by month — green for positive, red for negative, intensity by magnitude
  const maxAbs = Math.max(...monthlyData.map(m => Math.abs(m.pnl)), 1);

  return (
    <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold text-white flex items-center gap-2">
          <Calendar className="w-4 h-4 text-violet-400" />
          Monthly Returns
        </h4>
        <span className="text-xs font-mono text-gray-500">
          {monthlyData.length} month{monthlyData.length === 1 ? '' : 's'}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={monthlyData} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="key" tick={{ fontSize: 10, fill: '#6b7280' }} />
          <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => `$${v >= 0 ? '+' : ''}${(v / 1000).toFixed(1)}k`} />
          <Tooltip
            contentStyle={{ background: '#0d0e14', border: '1px solid #1f2937', fontSize: 11, borderRadius: 8 }}
            formatter={(v, _, p) => [`$${formatNum(v, 2)} (${p.payload.trades} trades)`, 'P&L']}
          />
          <ReferenceLine y={0} stroke="#6b7280" />
          <Bar dataKey="pnl">
            {monthlyData.map((m, i) => {
              const intensity = Math.min(1, Math.abs(m.pnl) / maxAbs);
              const color = m.pnl >= 0
                ? `rgba(16, 185, 129, ${0.4 + intensity * 0.5})`
                : `rgba(239, 68, 68, ${0.4 + intensity * 0.5})`;
              return <Cell key={i} fill={color} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function NoData({ label, reason }) {
  return (
    <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-6 text-center">
      <Activity className="w-6 h-6 text-gray-600 mx-auto mb-2" />
      <p className="text-sm text-white font-medium">{label}</p>
      <p className="text-xs text-gray-500 mt-1">{reason}</p>
    </div>
  );
}

// ─── Combined view ───────────────────────────────────────────────────────────

export default function BacktestVisualizations({ result, initialCapital = 10000 }) {
  const trades = result?.trades || [];
  return (
    <div className="space-y-3">
      <EquityCurveChart trades={trades} initialCapital={initialCapital} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <DrawdownChart trades={trades} initialCapital={initialCapital} />
        <MonthlyReturnsHeatmap trades={trades} initialCapital={initialCapital} />
      </div>
    </div>
  );
}
